import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar, RefreshCw, Lock, Unlock, AlertCircle, CheckCircle,
  ChevronRight, MoreHorizontal } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  open: 'bg-green-500/20 text-green-400 border-green-500/30',
  soft_close: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  closed: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export const PeriodClose = () => {const { t } = useTranslation("finance");
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCloseDialog, setShowCloseDialog] = useState(null);
  const [showReopenDialog, setShowReopenDialog] = useState(null);
  const [reopenReason, setReopenReason] = useState('');

  const fetchPeriods = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounting/periods`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch periods');
      const data = await res.json();
      setPeriods(data.periods || []);
    } catch (err) {
      toast.error(err.message);
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchPeriods();}, []);

  const handleClose = async (periodCode) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/periods/${periodCode}/close`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to close period');
      }
      toast.success(`Period ${periodCode} closed`);
      setShowCloseDialog(null);
      fetchPeriods();
    } catch (err) {toast.error(err.message);}
  };

  const handleReopen = async (periodCode) => {
    if (!reopenReason.trim()) {
      toast.error('Please provide a reason for reopening');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/accounting/periods/${periodCode}/reopen`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: reopenReason })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to reopen period');
      }
      toast.success(`Period ${periodCode} reopened`);
      setShowReopenDialog(null);
      setReopenReason('');
      fetchPeriods();
    } catch (err) {toast.error(err.message);}
  };

  const openCount = periods.filter((p) => p.status === 'open').length;
  const closedCount = periods.filter((p) => p.status === 'closed').length;
  const currentPeriod = periods.find((p) => p.status === 'open');

  return (
    <div className="space-y-6" data-testid="period-close-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("periodClose", "Period Close")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageFiscalPeriods", "Manage fiscal periods and period-end close process")}</p>
        </div>
        <Button variant="outline" onClick={fetchPeriods} className="gap-2">
          <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}
        </Button>
      </div>

      {/* Current Period Card */}
      {currentPeriod &&
      <Card className="border-green-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-lg bg-green-500/20">
                  <Calendar className="h-8 w-8 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("currentOpenPeriod", "Current Open Period")}</p>
                  <p className="text-2xl font-bold">{currentPeriod.period_code}</p>
                  <p className="text-sm text-muted-foreground">
                    {currentPeriod.start_date}{t("to", "to")}{currentPeriod.end_date}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowCloseDialog(currentPeriod)} className="gap-2 bg-amber-600 hover:bg-amber-700">
                <Lock className="h-4 w-4" />{t("closePeriod", "Close Period")}
            </Button>
            </div>
          </CardContent>
        </Card>
      }

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalPeriods", "Total Periods")}</p>
                <p className="text-3xl font-bold">{periods.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><Calendar className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("open", "Open")}</p>
                <p className="text-3xl font-bold">{openCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><Unlock className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("closed", "Closed")}</p>
                <p className="text-3xl font-bold">{closedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20"><Lock className="h-6 w-6 text-red-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Periods List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("fiscalPeriods", "Fiscal Periods")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          periods.length === 0 ?
          <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noFiscalPeriods", "No fiscal periods found")}</p>
              <p className="text-sm text-muted-foreground mt-2">{t("fiscalPeriodsNeed", "Fiscal periods need to be configured in the system")}</p>
            </div> :

          <div className="space-y-3">
              {periods.map((period) =>
            <div key={period.id} className={`flex items-center justify-between p-4 rounded-lg border ${
            period.status === 'open' ? 'bg-green-500/5 border-green-500/30' :
            period.status === 'closed' ? 'bg-slate-800/30 border-slate-700' :
            'bg-amber-500/5 border-amber-500/30'}`
            }>
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                period.status === 'open' ? 'bg-green-500/20' :
                period.status === 'closed' ? 'bg-slate-500/20' : 'bg-amber-500/20'}`
                }>
                      {period.status === 'open' ?
                  <Unlock className="h-5 w-5 text-green-500" /> :
                  period.status === 'closed' ?
                  <Lock className="h-5 w-5 text-slate-400" /> :

                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  }
                    </div>
                    <div>
                      <p className="font-semibold">{period.period_code}</p>
                      <p className="text-sm text-muted-foreground">
                        {period.start_date}{t("to", "to")}{period.end_date}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={statusColors[period.status]}>
                      {period.status?.replace('_', ' ')}
                    </Badge>
                    {period.closed_at &&
                <span className="text-xs text-muted-foreground">{t("closed", "Closed:")}
                  {new Date(period.closed_at).toLocaleDateString()}
                      </span>
                }
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {period.status === 'open' &&
                    <DropdownMenuItem onClick={() => setShowCloseDialog(period)}>
                            <Lock className="h-4 w-4 mr-2" />{t("closePeriod", "Close Period")}
                    </DropdownMenuItem>
                    }
                        {period.status === 'closed' &&
                    <DropdownMenuItem onClick={() => setShowReopenDialog(period)}>
                            <Unlock className="h-4 w-4 mr-2" />{t("reopenPeriod", "Reopen Period")}
                    </DropdownMenuItem>
                    }
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
            )}
            </div>
          }
        </CardContent>
      </Card>

      {/* Close Period Dialog */}
      <Dialog open={!!showCloseDialog} onOpenChange={() => setShowCloseDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("closePeriod", "Close Period")}</DialogTitle>
          </DialogHeader>
          {showCloseDialog &&
          <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-semibold">{t("warning", "Warning")}</span>
                </div>
                <p className="text-sm">{t("youAreAbout", "You are about to close period")}
                <strong>{showCloseDialog.period_code}</strong>{t("onceClosedNo", ".\n                  Once closed, no new postings can be made to this period without reopening it.")}

              </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-sm text-muted-foreground">{t("period", "Period")}</p>
                <p className="font-bold">{showCloseDialog.period_code}</p>
                <p className="text-sm text-muted-foreground">{showCloseDialog.start_date}{t("to", "to")}{showCloseDialog.end_date}</p>
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(null)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={() => handleClose(showCloseDialog.period_code)} className="bg-amber-600 hover:bg-amber-700">
              <Lock className="h-4 w-4 mr-2" />{t("closePeriod", "Close Period")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Period Dialog */}
      <Dialog open={!!showReopenDialog} onOpenChange={() => {setShowReopenDialog(null);setReopenReason('');}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("reopenPeriod", "Reopen Period")}</DialogTitle>
          </DialogHeader>
          {showReopenDialog &&
          <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center gap-2 text-blue-400 mb-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-semibold">{t("auditTrail", "Audit Trail")}</span>
                </div>
                <p className="text-sm">{t("aReasonIs", "A reason is required for reopening a closed period. This will be logged in the audit trail.")}

              </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-sm text-muted-foreground">{t("period", "Period")}</p>
                <p className="font-bold">{showReopenDialog.period_code}</p>
              </div>
              <div>
                <Label>{t("reasonForReopening", "Reason for Reopening *")}</Label>
                <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Enter the reason for reopening this period" />
              
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowReopenDialog(null);setReopenReason('');}}>{t("cancel", "Cancel")}</Button>
            <Button onClick={() => handleReopen(showReopenDialog.period_code)} className="bg-green-600 hover:bg-green-700">
              <Unlock className="h-4 w-4 mr-2" />{t("reopenPeriod", "Reopen Period")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default PeriodClose;